import { useI18n } from "@/composables/useI18n";
import { useTransferRequests } from "@/composables/useTransferRequests";
import { formatCurrency } from "@/utils/tools";
import {
  balanceAfterPendingTransfers,
  countsTowardBalanceAfter,
  getAccountBalance,
  getProviderType,
  transferBalanceImpact,
  type TransferForBalanceImpact,
} from "@aircap/common/account";
import { computed, ref, watch, type Ref } from "vue";

export const useBalanceImpact = (accountId: Ref<string>) => {
  const { locale } = useI18n();
  const { transferRequests, loadingTransferRequests, account } = useTransferRequests(accountId);

  // VueFire keeps the previous transfer list until the next snapshot (`reset: false`).
  // Clear the settled id on every account change and set it only afterwards, once this
  // account's document is in and a snapshot has finished. A balance tick on the same account
  // also flips `pending`; leaving the id in place keeps the line up, because that list is
  // still this account's.
  const settledAccountId = ref<string | undefined>();
  watch(
    [accountId, loadingTransferRequests, () => account.value?.id],
    ([id, loading, loadedId], previous) => {
      if (!previous || previous[0] !== id || loadedId !== id) {
        settledAccountId.value = undefined;
        return;
      }
      if (loading) return;
      if (previous[1] || previous[2] !== loadedId) settledAccountId.value = id;
    },
  );

  const booked = computed(() => {
    if (!account.value || account.value.id !== accountId.value) return undefined;
    if (settledAccountId.value !== accountId.value) return undefined;
    if (getProviderType(account.value.accountType) !== "proAccount") return undefined;
    return getAccountBalance(account.value);
  });

  const inputs = computed(() => {
    if (!booked.value) return [];
    return (transferRequests.value ?? []).map((request): TransferForBalanceImpact => ({
      status: request.status,
      amount: request.amount,
    }));
  });

  const hasPendingTransfers = computed(() =>
    inputs.value.some((request) => countsTowardBalanceAfter(request.status)),
  );
  const balanceAfterPending = computed(() => {
    if (booked.value?.balance === undefined) return null;
    return balanceAfterPendingTransfers(booked.value.balance, inputs.value);
  });

  const formatBalance = (value: number) => {
    if (!booked.value?.currency) return "";
    return formatCurrency({
      value,
      currencyCode: booked.value.currency,
      locale: locale.value,
    });
  };

  const formattedBalanceAfterPending = computed(() =>
    balanceAfterPending.value === null ? null : formatBalance(balanceAfterPending.value),
  );

  const impactFor = (request: {
    id: string;
    status: TransferForBalanceImpact["status"];
    amount: string;
  }) => {
    const realBalance = booked.value?.balance;
    if (realBalance === undefined) return undefined;
    if (!transferRequests.value?.some((item) => item.id === request.id)) return undefined;
    const impact = transferBalanceImpact(realBalance, request);
    if (!impact) return undefined;
    return {
      before: impact.before,
      after: impact.after,
      formattedBefore: formatBalance(impact.before),
      formattedAfter: formatBalance(impact.after),
    };
  };

  return {
    hasPendingTransfers,
    formattedBalanceAfterPending,
    impactFor,
  };
};
